import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select, { components } from "react-select";

import { comma, formatDate, useFormInput } from "@/utils";
import { Confirm, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import { useLazySearchCarQuery, usePostCarMutation, usePutCarMutation } from "@/redux/carApi";
import { useGetBranchInfoQuery, useGetMyRentCorpQuery } from "@/redux/rentCorpApi";
import { useSetRentCorpPrimaryAdminMutation } from "@/redux/adminRentCorpApi";


import { useGetCarLocationListQuery } from "@/redux/carLocationApi";
import { usePostParkingLotMutation } from "@/redux/parkingLotApi";
import { branchTypeOptions, corpTypeOptions, memberStatusOptions, rolesOptions } from "@/constants/selectOptions";
import { useGetAddressCode2Mutation, useGetAddressCodeQuery, useGetBankListQuery } from "@/redux/metaApi";
import {
    useDeleteMemberMutation,
    useGetMemberDetailQuery,
    useGetMemberListQuery,
    useMemberResetPasswordMutation,
    useUpdateMemberMutation,
} from "@/redux/adminMemberApi";
import { useAuthChangePasswordMutation } from "@/redux/authApi";

import { useUpdateRentCorpAdminMutation } from "@/redux/adminRentCorpApi";
import ReactDOM from "react-dom";
import { formatPhoneNumber } from "../../utils";

// 비밀번호 초기화 팝업 컴포넌트
const ResetPasswordPopup = ({ onClose, memberId }) => {
    const [authChangePassword] = useAuthChangePasswordMutation();

    const { formData, handleChange, setFormData } = useFormInput({
        toastMsg: "",
        password: "",
        passwordCheck: "",
    });

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    const validatePassword = (password, passwordCheck) => {
        if (!password) return "비밀번호를 입력해주세요.";
        if (password !== passwordCheck) return "비밀번호가 일치하지 않습니다.";

        const hasLetter = /[a-zA-Z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < 8 || !hasLetter || !hasNumber || !hasSpecial) {
            return "비밀번호는 영문, 숫자, 특수문자를 모두 포함한 8자 이상으로 설정해주세요.";
        }
        return "";
    };

    const handleResetPassword = async () => {
        const errMsg = validatePassword(formData.password, formData.passwordCheck);
        if (errMsg) {
            setFormData((prev) => ({ ...prev, toastMsg: errMsg }));
            return;
        }

        try {
            await authChangePassword({ memberId, password: formData.password }).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "비밀번호가 성공적으로 변경되었습니다.",
            }));
            setTimeout(() => onClose(), 1500);
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "비밀번호 변경에 실패했습니다.",
            }));
        }
    };

    return ReactDOM.createPortal(
        <>
            {formData.toastMsg && <Toast visible={true} msg={formData.toastMsg} setFormData={setFormData} />}

            <div className="layer active" onClick={onClose}></div>

            <div className="popup input-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>비밀번호 초기화</span>
                    </div>
                    <div className="content-description">
                        <p>새로운 비밀번호를 입력해주세요.</p>
                    </div>
                    <div className="content-inputs">
                        <div className="input-group">
                            <label>새 비밀번호</label>
                            <div className="input-wrapper">
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="새 비밀번호 입력"
                                />
                            </div>
                        </div>
                        <div className="input-group">
                            <label>비밀번호 확인</label>
                            <div className="input-wrapper">
                                <input
                                    type="password"
                                    name="passwordCheck"
                                    value={formData.passwordCheck}
                                    onChange={handleChange}
                                    placeholder="비밀번호 확인"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleResetPassword}
                        disabled={!formData.password || !formData.passwordCheck}
                    >
                        비밀번호 변경
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

export const AdminMemberRegister = () => {
    const { memberId } = useParams();
    const navigate = useNavigate();
    const [resetPasswordPopupVisible, setResetPasswordPopupVisible] = useState(false);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "READ" / "UPDATE"
        isLoading: false,
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,
        member: {},
        rentCorps: [],
        selectedRentCorp: {},
    });
    const { mode, isLoading, toastMsg, confirmStatus, addressPopupStatus, id } = formData;

    const { data: memberDetailData, refetch: refetchMemberDetail } = useGetMemberDetailQuery(memberId, {
        skip: !memberId,
    });

    const [updateMember] = useUpdateMemberMutation();
    const [memberResetPassword] = useMemberResetPasswordMutation();

    const {
        data: carDetailData,
        isFetching: isCarDetailFetching,
        error: carDetailError,
    } = useGetMemberListQuery(memberId, {
        skip: !memberId,
    });

    const fetchMemberDetail = async (memberId) => {
        setIsDetailLoading(true);
        try {
            const response = await refetchMemberDetail();
            setFormData((prev) => ({
                ...prev,
                member: response.data.data.member,
                rentCorps: response.data.data.rentCorps,
                selectedRentCorp: response.data.data.rentCorps?.length > 0 ? response.data.data.rentCorps[0] : {},
                rentCorpAreas: response.data.data.rentCorpAreas || [],
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "회원 정보 조회에 실패했습니다.",
            }));
        } finally {
            setIsDetailLoading(false);
        }
    };

    // 모드 설정: memberId 있으면 "UPDATE"
    useEffect(() => {
        if (memberId) {
            fetchMemberDetail(memberId);
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: memberId,
            }));
        }
    }, [memberId, setFormData]);

    // API 호출
    const [postCar, { isLoading: isPostLoading }] = usePostCarMutation();
    const [putCar, { isLoading: isPutLoading }] = usePutCarMutation();
    const [postParkingLot] = usePostParkingLotMutation();
    const [doSearchCar, { data: searchCarData, isFetching, error }] = useLazySearchCarQuery();
    const [deleteMember] = useDeleteMemberMutation();

    const handleDeleteConfirm = async () => {
        if (!formData?.id) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "삭제할 회원이 없습니다.",
            }));
            return;
        }

        try {
            await deleteMember(formData?.id).unwrap();

            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "회원 정보 삭제",
                    confirmDescription: "회원이 성공적으로 삭제되었습니다.",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: () => {
                                navigate("/erp/member/manage");
                            },
                        },
                    ],
                },
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `${formData.member.name} 회원님의 모든 데이터를 삭제하시겠습니까?`,
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    useEffect(() => {
        if (memberId) {
            setFormData((prev) => ({
                ...prev,
                mode: "READ",
                id: memberId,
            }));
        }
    }, [memberId, setFormData]);

    const handleSave = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "회원 정보 수정",
                confirmDescription: "회원 정보를 수정하시겠습니까?",
                confirmButtons: [
                    {
                        label: "취소",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev) => ({
                                ...prev,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                    {
                        label: "확인",
                        className: "btn-primary",
                        onClick: async () => {
                            try {
                                const requestBody = {
                                    loginId: formData.member?.loginId,
                                    idNumber: formData.member?.idNumber,
                                    name: formData.member?.name,
                                    phone: formData.member?.phone,
                                    roles: formData.member?.roles || ["MASTER"],
                                    rentCorp: {
                                        id: formData.selectedRentCorp?.id,
                                        corpName: formData.selectedRentCorp?.corpName,
                                        bankName: formData.selectedRentCorp?.bankName,
                                        bankAccount: formData.selectedRentCorp?.bankAccount,
                                        bankOwner: formData.selectedRentCorp?.bankOwner,
                                    },
                                    memo: formData.memo,
                                    firstAddressCodeId: formData.member?.firstAddressCodeId,
                                    secondAddressCodeId: formData.member?.secondAddressCodeId,
                                    branchType: formData.member?.branchType,
                                };
                                console.log("Request payload:", { memberId, requestBody });

                                await updateMember({ memberId, body: requestBody }).unwrap();

                                setFormData((prev) => ({
                                    ...prev,
                                    toastMsg: "회원 정보가 성공적으로 수정되었습니다.",
                                    confirmStatus: { confirmPopupStatus: false },
                                }));
                            } catch (error) {
                                console.error("API Error:", error);
                                setFormData((prev) => ({
                                    ...prev,
                                    toastMsg: error?.message || "회원 정보 수정에 실패했습니다.",
                                    confirmStatus: { confirmPopupStatus: false },
                                }));
                            }
                        },
                    },
                ],
            },
        }));
    };

    const handleResetPassword = async () => {
        try {
            await memberResetPassword(memberId).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "비밀번호가 성공적으로 초기화되었습니다.",
            }));
            setResetPasswordPopupVisible(false);
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "비밀번호 초기화에 실패했습니다.",
            }));
        }
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">{mode === "CREATE" ? "회원 등록" : "회원 상세"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>
                                이전으로
                            </button>
                            {mode !== "CREATE" && (
                                <>
                                    <button type="button" className="btn btn-secondary" onClick={() => setResetPasswordPopupVisible(true)}>
                                        비밀번호 초기화
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                className="btn btn-fourth"
                                onClick={() =>
                                    navigate(`/erp/address-management/edit/${memberId}`, {
                                        state: {
                                            memberId,
                                            rentCorpAreas: formData.rentCorpAreas,
                                        },
                                    })
                                }
                            >
                                지역 관리
                            </button>
                            <button type="button" className="btn btn-cancel" onClick={handleDelete}>
                                회원 삭제
                            </button>
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기본 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <MemberInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">사업자 선택</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <CorpInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            fetchMemberDetail={fetchMemberDetail}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">사업자 설정</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <CorpSetting formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">업로드 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <UploadInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계좌 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <BankInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">메모사항</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <MemoInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            parkingLotAddress: data.address,
                            parkingLotBCode: data.bcode,
                        }))
                    }
                />
            )}

            {/* 비밀번호 초기화 팝업 */}
            {resetPasswordPopupVisible && <ResetPasswordPopup onClose={() => setResetPasswordPopupVisible(false)} memberId={memberId} />}
        </>
    );
};

/* 기본 정보 */
const MemberInfo = ({ formData, handleChange, setFormData, handleSearchCar }) => {
    const { ownerName, vehicleOrigin, color, roles } = formData;

    useEffect(() => {
        console.log(formData.member?.roles);
    }, [formData]);

    return (
        <>
            {/* 로그인 ID */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>로그인 ID</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="member.loginId"
                            placeholder=""
                            value={formData.member?.loginId || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            {/* 회원상태 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>회원상태</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={memberStatusOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder=""
                            value={memberStatusOptions.find((opt) => opt.value === formData.member?.memberStatus)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, vehicleOrigin: option.value }))}
                            isDisabled
                        />
                    </div>
                </div>
            </div>

            {/* 포인트 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>마일리지</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="member.mileage"
                            placeholder=""
                            value={comma(formData.member?.mileage || "0")}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>

            {/* 이름 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>이름</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="member.name" placeholder="" value={formData.member?.name || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 연락처 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="member.phone"
                            placeholder=""
                            value={formatPhoneNumber(formData.member?.phone || "")}
                            onChange={(e) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    member: {
                                        ...prev.member,
                                        phone: e.target.value.replaceAll("-", ""),
                                    }
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 주민등록번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>주민등록번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="member.idNumber"
                            placeholder=""
                            value={formData.member?.idNumber || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            {/* 권한 설정 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>권한 설정</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            isMulti
                            options={rolesOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="권한 선택"
                            value={rolesOptions.filter((option) => formData.member?.roles?.includes(option.value))}
                            onChange={(selectedOptions) => {
                                const newRoles = selectedOptions ? selectedOptions.map((option) => option.value) : [];
                                setFormData((prev) => ({
                                    ...prev,
                                    member: {
                                        ...prev.member,
                                        roles: newRoles,
                                    },
                                }));
                            }}
                            styles={{
                                container: (provided) => ({
                                    ...provided,
                                }),
                                control: (provided) => ({
                                    ...provided,
                                    height: "unset !important",
                                }),
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const UploadInfo = ({ formData, handleChange, setFormData }) => {
    const handleFileClick = (objectUrl) => {
        if (objectUrl) {
            window.open(objectUrl, "_blank");
        }
    };

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자등록증</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="ownerName"
                            placeholder=""
                            value={
                                formData.selectedRentCorp?.rentCorpUploads?.find((item) => item.uploadType === "BIZ_LIC")?.upload
                                    ?.fileName || ""
                            }
                            onClick={() =>
                                handleFileClick(
                                    formData.selectedRentCorp?.rentCorpUploads?.find((item) => item.uploadType === "BIZ_LIC")?.upload
                                        ?.objectUrl
                                )
                            }
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>통장사본</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="ownerName"
                            placeholder=""
                            value={
                                formData.selectedRentCorp?.rentCorpUploads?.find((item) => item.uploadType === "BANK")?.upload?.fileName ||
                                ""
                            }
                            onClick={() =>
                                handleFileClick(
                                    formData.selectedRentCorp?.rentCorpUploads?.find((item) => item.uploadType === "BANK")?.upload
                                        ?.objectUrl
                                )
                            }
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>법인인감</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="ownerName"
                            placeholder=""
                            value={
                                formData.selectedRentCorp?.rentCorpUploads?.find((item) => item.uploadType === "CORP_SEAL")?.upload
                                    ?.fileName || ""
                            }
                            onClick={() =>
                                handleFileClick(
                                    formData.selectedRentCorp?.rentCorpUploads?.find((item) => item.uploadType === "CORP_SEAL")?.upload
                                        ?.objectUrl
                                )
                            }
                            style={{ cursor: "pointer" }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const BankInfo = ({ formData, handleChange, setFormData }) => {
    const { bankName, ownerName, carNumber, vehicleOrigin, color } = formData;

    // API 호출: 은행리스트 조회
    const { data: bankData, isLoading: bankLoading } = useGetBankListQuery();

    // 은행 Select 예시
    const bankOptions = bankData?.data
        ? bankData.data.map((bank) => ({
            value: bank.code,
            label: bank.name,
            icon: bank.imgUrl,
        }))
        : [];

    // 커스텀 옵션: 드롭다운에서는 이미지와 라벨 모두 표시
    const IconOption = (props) => (
        <components.Option {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} />
            {props.data.label}
        </components.Option>
    );

    // 커스텀 SingleValue: 선택된 값은 라벨만 표시
    const CustomSingleValue = (props) => (
        <components.SingleValue {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} /> {props.data.label}
        </components.SingleValue>
    );

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>예금주</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="selectedRentCorp.bankOwner"
                            placeholder=""
                            value={formData.selectedRentCorp?.bankOwner || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>은행</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={bankOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="은행 선택"
                            value={bankOptions.find((opt) => opt.value === formData.selectedRentCorp?.bankName)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    selectedRentCorp: {
                                        ...prev.selectedRentCorp,
                                        bankName: option.value,
                                    },
                                }))
                            }
                            components={{ Option: IconOption, SingleValue: CustomSingleValue }}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>계좌번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="selectedRentCorp.bankAccount"
                            placeholder=""
                            value={formData.selectedRentCorp?.bankAccount || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const CorpInfo = ({ formData, handleChange, setFormData, fetchMemberDetail }) => {
    const { rentCorpId, branchId, carLocationId, carStatus, ownerName } = formData;
    const [setRentCorpPrimary] = useSetRentCorpPrimaryAdminMutation();

    const [updateRentCorpAdmin] = useUpdateRentCorpAdminMutation();

    // API 호출: 나의 소속 렌트카 업체 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        formData.rentCorps && formData.rentCorps.length > 0
            ? formData.rentCorps.map((item) => ({
                value: item.id,
                label: item.corpName,
            }))
            : [];

    const { data: branchData } = useGetBranchInfoQuery(rentCorpId, {
        skip: !rentCorpId,
    });
    const branchOptions =
        branchData && branchData.data
            ? branchData.data.map((item) => ({
                value: item.id,
                label: item.corpName,
            }))
            : [];

    const { data: carLocationData } = useGetCarLocationListQuery();
    const carLocationOptions =
        carLocationData && carLocationData.data
            ? carLocationData.data.map((item) => ({
                value: item.id,
                label: item.name,
            }))
            : [];

    // 사업자명 저장 핸들러
    const handleCorpNameSave = async () => {
        if (!formData.selectedRentCorp?.id) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "선택된 사업자가 없습니다.",
            }));
            return;
        }

        if (!formData.selectedRentCorp?.corpName?.trim()) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자명을 입력해주세요.",
            }));
            return;
        }

        try {
            const requestBody = {
                corpName: formData.selectedRentCorp.corpName,
            };

            await updateRentCorpAdmin({
                rentCorpId: formData.selectedRentCorp.id,
                body: requestBody,
            }).unwrap();

            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자명이 성공적으로 수정되었습니다.",
            }));

            // 사업자명 수정 후 회원 상세 정보 다시 조회
            await fetchMemberDetail(formData.id);
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "사업자명 수정에 실패했습니다.",
            }));
        }
    };

    // 대표사업자 설정 핸들러
    const handlePrimaryYnChange = async (value) => {
        if (value === "Y") {
            // 이미 대표사업자로 설정되어 있는 경우
            if (formData.selectedRentCorp?.primaryYn === "Y") {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "이미 대표사업자로 설정되어 있습니다.",
                }));
                return;
            }

            // 확인 모달 표시
            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "대표사업자 설정",
                    confirmDescription: "대표사업자로 설정하시겠습니까?\n다른 사업자의 대표 설정이 비활성됩니다.",
                    confirmButtons: [
                        {
                            label: "취소",
                            className: "btn-cancel",
                            onClick: () =>
                                setFormData((prev) => ({
                                    ...prev,
                                    confirmStatus: { confirmPopupStatus: false },
                                })),
                        },
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: async () => {
                                try {
                                    await setRentCorpPrimary(formData.selectedRentCorp?.id).unwrap();

                                    // 대표사업자 설정 후 회원 상세 정보 다시 조회
                                    await fetchMemberDetail(formData.id);

                                    setFormData((prev) => ({
                                        ...prev,
                                        confirmStatus: { confirmPopupStatus: false },
                                        toastMsg: "대표사업자로 설정되었습니다.",
                                    }));
                                } catch (error) {
                                    setFormData((prev) => ({
                                        ...prev,
                                        confirmStatus: { confirmPopupStatus: false },
                                        toastMsg: error?.message || "대표사업자 설정에 실패했습니다.",
                                    }));
                                }
                            },
                        },
                    ],
                },
            }));
        } else if (value === "N") {
            // 현재 대표사업자인 상태에서 비활성 클릭 시
            if (formData.selectedRentCorp?.primaryYn === "Y") {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "대표사업자를 비활성화할 수 없습니다. 다른 사업자에서 대표사업자를 활성화한 후 시도해주세요.",
                }));
                return;
            }
        }
    };

    return (
        <>
            {/* 사업자 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 선택</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={corpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="사업자 선택"
                            value={corpOptions.find((opt) => opt.value === formData.selectedRentCorp?.id)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    rentCorpId: option.value,
                                    selectedRentCorp: formData.rentCorps?.find((opt) => opt.id === option.value),
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자명</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="selectedRentCorp.corpName"
                            placeholder=""
                            value={formData.selectedRentCorp?.corpName || ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={handleCorpNameSave}>
                            저장
                        </button>
                    </div>
                </div>
            </div>

            {/* 대표사업자 여부 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>대표사업자 여부</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="primaryYn"
                                id="primaryYn_Y"
                                checked={formData.selectedRentCorp.primaryYn === "Y"}
                                onChange={(e) => {
                                    handlePrimaryYnChange("Y");
                                }}
                            />
                            <label htmlFor="primaryYn_Y">활성</label>
                        </div>
                        {
                            /*
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="primaryYn"
                                id="primaryYn_N"
                                checked={formData.selectedRentCorp.primaryYn === "N"}
                                onChange={(e) => {
                                    handlePrimaryYnChange("N");
                                }}
                            />
                            <label htmlFor="primaryYn_N">비활성</label>
                        </div>
                             */
                        }

                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 구분</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={corpTypeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="사업자 구분"
                            value={corpTypeOptions.find((opt) => opt.value === formData.selectedRentCorp?.corpType)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, branchId: option.value }))}
                            isDisabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>법인 등록번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="selectedRentCorp.corpNumber"
                            placeholder=""
                            value={formData.selectedRentCorp?.corpNumber || ""}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 등록번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="selectedRentCorp.bizNumber"
                            placeholder=""
                            value={formData.selectedRentCorp?.bizNumber || ""}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>대표자명</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="selectedRentCorp.repName"
                            placeholder=""
                            value={formData.selectedRentCorp?.repName || ""}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>업태</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="selectedRentCorp.bizType"
                            placeholder=""
                            value={formData.selectedRentCorp?.bizType || ""}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>종목</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="selectedRentCorp.bizCategory"
                            placeholder=""
                            value={formData.selectedRentCorp?.bizCategory || ""}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>개업연월일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        {/* <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={formData.selectedRentCorp?.bizStartedAt}
                            onChange={(date) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    selectedRentCorp: {
                                        ...prev.selectedRentCorp,
                                        bizStartedAt: date,
                                    },
                                }));
                            }}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                            disabled
                        /> */}
                        <input
                            type="text"
                            name="selectedRentCorp.bizStartedAt"
                            value={formatDate(formData.selectedRentCorp?.bizStartedAt)}
                            placeholder=""
                            onChange={handleChange}
                            maxLength={10}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>사업장 주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ width: "200px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="parkingLotBCode"
                            value={""}
                            onChange={handleChange}
                            readOnly
                            onClick={(e) => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                }));
                            }}
                            disabled
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="parkingLotAddress"
                            value={formData?.selectedRentCorp?.address || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                }));
                            }}
                            disabled
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="parkingLotAddressDetail"
                            value={formData?.selectedRentCorp?.addressDetail || ""}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                    {/* <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                }));
                            }}
                        >
                            주소검색
                        </button>
                    </div> */}
                </div>
            </div>
        </>
    );
};

const CorpSetting = ({ formData, handleChange, setFormData }) => {
    const { data: loc1, isLoading: isLoadingLoc1 } = useGetAddressCodeQuery({ take_1_step: true, take_2_step: false });
    const [getAddressCode2, { data: loc2, isLoadingLoc2 }] = useGetAddressCode2Mutation();

    // 상세 조회 시 1차 지역 ID가 있으면 자동으로 2차 지역 데이터 조회
    useEffect(() => {
        if (formData.member?.firstAddressCodeId && loc1?.data) {
            const firstAddressItem = loc1.data.find((item) => item.id === formData.member.firstAddressCodeId);
            if (firstAddressItem) {
                getAddressCode2({ take_1_step: false, take_2_step: true, parent_b_code: firstAddressItem.bCode });
            }
        }
    }, [formData.member?.firstAddressCodeId, loc1?.data, getAddressCode2]);

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>지역 설정</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={loc1?.data.map((item) => ({
                                value: item.id,
                                label: item.address1,
                                bcode: item.bCode,
                            }))}
                            value={
                                formData.member?.firstAddressCodeId
                                    ? loc1?.data
                                        .map((item) => ({
                                            value: item.id,
                                            label: item.address1,
                                            bcode: item.bCode,
                                        }))
                                        .find((option) => option.value === formData.member?.firstAddressCodeId)
                                    : null
                            }
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable
                            placeholder="1차 지역"
                            onChange={(option) => {
                                getAddressCode2({ take_1_step: false, take_2_step: true, parent_b_code: option.bcode });
                                setFormData((prev) => ({
                                    ...prev,
                                    member: {
                                        ...prev.member,
                                        firstAddressCodeId: option.value,
                                        secondAddressCodeId: null,
                                    },
                                }));
                            }}
                        />
                    </div>
                    <div className="form-element">
                        <Select
                            options={loc2?.data.map((item) => ({
                                value: item.id,
                                label: item.address2,
                                bcode: item.bCode,
                            }))}
                            value={
                                formData.member?.secondAddressCodeId
                                    ? loc2?.data
                                        .map((item) => ({
                                            value: item.id,
                                            label: item.address2,
                                            bcode: item.bCode,
                                        }))
                                        .find((option) => option.value === formData.member?.secondAddressCodeId)
                                    : null
                            }
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable
                            placeholder="2차 지역"
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    member: {
                                        ...prev.member,
                                        secondAddressCodeId: option.value,
                                    },
                                }));
                            }}
                            isDisabled={!formData.member?.firstAddressCodeId}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>지역 구분</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={branchTypeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="선택"
                            value={branchTypeOptions.find((option) => option.value === formData.member?.branchType)}
                            onChange={(option) =>
                                setFormData((prev) => ({ ...prev, member: { ...prev.member, branchType: option.value } }))
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const MemoInfo = ({ formData, handleChange, setFormData }) => {
    const { memo } = formData;

    return (
        <>
            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="memo" placeholder="메모를 작성해주세요." value={memo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};
